import * as THREE from 'three';
import type { City, Place } from '../world/city';
import { railSample, railLayouts, railStations, type RailLayout, type RailStation } from '../world/rail';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import { Reservations } from './reservations';

interface Train {
  layout: RailLayout;
  closing?: boolean;
  id: string;
  cars: TrafficVehicle[];
  distance: number;
  speed: number;
  state: 'running' | 'dwell' | 'stopped' | 'derailed';
  station: number;
  next: number;
  dwell: number;
  visits: number;
  circuits: number;
  reason: string;
}
interface Ticket {
  nextLegs?: { origin: RailStation; destination: RailStation }[];
  person: Person;
  origin: RailStation;
  destination: RailStation;
  queue: Place;
  final: Place;
  state: 'approaching' | 'waiting' | 'boarding' | 'aboard' | 'alighting';
  train?: Train;
  carriage?: number;
  progress: number;
  since: number;
  from?: THREE.Vector3;
  boardingRoute?: number[];
}
/** Timetabled transport carries original residents. Track geometry never becomes an immortal route. */
export class Railway {
  trains: Train[] = [];
  tickets = new Map<string, Ticket>();
  log: { time: number; train?: string; person?: string; action: string; station?: string }[] = [];
  headway = 90;
  private time = 0;
  private epoch = 0;
  private lastDeparture = new Map<string, number>();
  private integrity = new Map<number, { time: number; open: boolean }>();
  readonly reservations: Reservations;
  readonly layout: RailLayout;
  readonly layouts: RailLayout[];
  boardings = 0;
  arrivals = 0;
  transfers = 0;
  disrupted = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.layout = city.rail!;
    this.layouts = railLayouts(city.rail);
    this.reservations = new Reservations(() => physics.life.epoch);
    this.reset();
  }
  private record(action: string, train?: Train, person?: Person, station?: RailStation): void {
    if (train && ['arrived', 'departed'].includes(action))
      this.population.audio.play(action === 'arrived' ? 'brake' : 'door', 0.8, train.cars[0].p);
    this.log.push({ time: this.time, action, train: train?.id, person: person?.id, station: station?.name });
    if (this.log.length > 512) this.log.shift();
  }
  clear(): void {
    this.trains = [];
    this.tickets.clear();
    this.reservations.clear();
    this.lastDeparture.clear();
    this.integrity.clear();
    this.log = [];
    this.time = this.boardings = this.arrivals = this.transfers = this.disrupted = 0;
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    for (const layout of this.layouts)
      for (let i = 0; i < 2; i++) {
        const station = i ? 3 : 0,
          s = layout.stations[station],
          distance = s.distance + 12.6;
        const train: Train = {
          id: layout === this.layout ? `train:${i}` : `train:${layout.id}:${i}`,
          layout,
          cars: [],
          distance,
          speed: 0,
          state: 'dwell',
          station,
          next: (station + 1) % layout.stations.length,
          dwell: 12,
          visits: 0,
          circuits: 0,
          reason: 'Doors open at platform',
        };
        for (let j = 0; j < 3; j++) {
          const sample = railSample(layout, distance - j * 12.6),
            car = this.population.createCar(`${train.id}:car:${j}`, 'train', sample.point, 0);
          car.name = `${layout.name ?? 'Coastal Line'} ${i + 1} · carriage ${j + 1}`;
          car.state = 'rail';
          car.controlled = true;
          car.color = i ? 0xb8ccc1 : 0xc5d9d1;
          car.yaw = Math.atan2(-sample.direction.x, -sample.direction.z);
          car.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), car.yaw);
          car.previousQ.copy(car.q);
          car.body.setRotation(car.q, false);
          train.cars.push(car);
        }
        this.trains.push(train);
      }
  }
  invalidate(buildingId: number): void {
    this.integrity.delete(buildingId);
  }
  private fixedCell(id: number): boolean {
    const owner = this.physics.cellOwner.get(id);
    return (
      owner !== undefined && !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
    );
  }
  sectionOpen(id: number, layout = this.layout): boolean {
    const section = layout.sections[id];
    const prior = this.integrity.get(section.buildingId);
    if (prior && this.time - prior.time < 0.25) return prior.open;
    const open = section.cells.every((id) => this.fixedCell(id));
    this.integrity.set(section.buildingId, { time: this.time, open });
    return open;
  }
  stationOpen(station: RailStation): boolean {
    const layout = this.stationLine(station);
    return (
      station.queues.some((p) => this.fixedCell(p.supportCell!)) &&
      this.sectionOpen(railSample(layout, station.distance).section.id, layout)
    );
  }
  private stationLine(station: RailStation): RailLayout {
    return this.layouts.find((line) => line.id === station.service) ?? this.layout;
  }
  private forward(from: number, to: number, layout = this.layout): number {
    return (((to - from) % layout.length) + layout.length) % layout.length;
  }
  book(person: Person, final: Place): Place | undefined {
    if (this.tickets.has(person.id)) return this.tickets.get(person.id)!.queue;
    if (Math.hypot(final.x - person.p.x, final.z - person.p.z) < 220) return;
    const nearest = (point: { x: number; z: number }) =>
      railStations(this.layout)
        .filter((s) => this.stationOpen(s))
        .sort(
          (a, b) =>
            Math.hypot(a.entrance.x - point.x, a.entrance.z - point.z) -
            Math.hypot(b.entrance.x - point.x, b.entrance.z - point.z),
        )[0];
    const origin = nearest(person.p),
      destination = nearest(final);
    if (!origin || !destination || origin === destination) return;
    const originLine = this.stationLine(origin),
      destinationLine = this.stationLine(destination);
    const legs: { origin: RailStation; destination: RailStation }[] = [];
    let transferWalk = 0;
    if (originLine === destinationLine) legs.push({ origin, destination });
    else {
      const changeFrom = originLine.stations.find(
        (s) =>
          s.interchange &&
          this.stationOpen(s) &&
          destinationLine.stations.some(
            (other) => other.interchange === s.interchange && this.stationOpen(other),
          ),
      );
      const changeTo =
        changeFrom && destinationLine.stations.find((s) => s.interchange === changeFrom.interchange);
      if (!changeFrom || !changeTo) return;
      if (origin !== changeFrom) legs.push({ origin, destination: changeFrom });
      transferWalk = Math.hypot(
        changeFrom.entrance.x - changeTo.entrance.x,
        changeFrom.entrance.z - changeTo.entrance.z,
      );
      if (changeTo !== destination) legs.push({ origin: changeTo, destination });
      if (!legs.length) return;
    }
    const first = Math.hypot(person.p.x - origin.entrance.x, person.p.z - origin.entrance.z),
      last = Math.hypot(final.x - destination.entrance.x, final.z - destination.entrance.z);
    if (
      first > 270 ||
      last > 270 ||
      first +
        last +
        transferWalk +
        legs.reduce(
          (sum, leg) =>
            sum +
            this.forward(leg.origin.distance, leg.destination.distance, this.stationLine(leg.origin)) / 10,
          0,
        ) >
        Math.hypot(final.x - person.p.x, final.z - person.p.z) - 45
    )
      return;
    const firstLeg = legs.shift()!;
    const queue = firstLeg.origin.queues.find(
      (p) =>
        this.population.civil!.network.available(p) &&
        this.reservations.acquire(p.id, person.id, this.time, 1, 600),
    );
    if (!queue) return;
    this.tickets.set(person.id, {
      person,
      origin: firstLeg.origin,
      destination: firstLeg.destination,
      nextLegs: legs,
      queue,
      final,
      state: 'approaching',
      progress: 0,
      since: this.time,
    });
    this.record('walking to station', undefined, person, firstLeg.origin);
    return queue;
  }
  waitAtPlatform(person: Person): boolean {
    const ticket = this.tickets.get(person.id);
    if (!ticket) return false;
    if (!this.stationOpen(ticket.origin)) {
      this.cancel(person.id, 'Station disrupted');
      return false;
    }
    if (
      ticket.state === 'approaching' &&
      person.p.distanceTo(new THREE.Vector3(ticket.queue.x, ticket.queue.y, ticket.queue.z)) < 0.5
    ) {
      ticket.state = 'waiting';
      ticket.since = this.time;
      person.state = 'waiting for train';
      this.record('waiting on platform', undefined, person, ticket.origin);
    }
    return true;
  }
  cancel(id: string, reason: string): void {
    const ticket = this.tickets.get(id);
    if (!ticket) return;
    this.reservations.release(id);
    this.tickets.delete(id);
    ticket.person.controlled = false;
    ticket.person.hidden = false;
    this.record(reason, ticket.train, ticket.person);
    if (!['incapacitated', 'falling', 'flee'].includes(ticket.person.state))
      this.population.civil?.finishTransit(ticket.person, ticket.final, reason);
  }
  private passengers(train: Train): Ticket[] {
    return [...this.tickets.values()].filter(
      (t) => t.train === train && ['boarding', 'aboard', 'alighting'].includes(t.state),
    );
  }
  private door(ticket: Ticket): THREE.Vector3 {
    const car = ticket.train!.cars[ticket.carriage!],
      station = ticket.state === 'alighting' ? ticket.destination : ticket.origin;
    return car.p
      .clone()
      .add(new THREE.Vector3(station.side.x, 0, station.side.z).multiplyScalar(1.85))
      .setY(station.lift.y);
  }
  private derail(train: Train, reason: string): void {
    if (train.state === 'derailed') return;
    train.state = 'derailed';
    train.reason = reason;
    this.disrupted++;
    this.record(reason, train);
    for (const car of train.cars) {
      this.population.disable(car);
      car.health = 0;
    }
    for (const ticket of this.passengers(train)) {
      const p = ticket.person,
        car = train.cars[ticket.carriage!];
      p.p.copy(car.p).add(new THREE.Vector3(0, 0.55, 0));
      p.previous.copy(p.p);
      p.velocity.copy(car.body.linvel());
      p.state = 'incapacitated';
      p.layer = 'rail incident';
      this.cancel(p.id, 'Passenger awaiting rail rescue');
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    for (const train of this.trains) {
      if (train.state === 'derailed') continue;
      if (train.cars.some((c) => c.state === 'wreck')) {
        this.derail(train, 'Rolling stock damaged');
        continue;
      }
      if (
        train.cars.some(
          (c) =>
            !this.sectionOpen(
              railSample(train.layout, train.distance - train.cars.indexOf(c) * 12.6).section.id,
              train.layout,
            ),
        )
      ) {
        this.derail(train, 'Track support lost under train');
        continue;
      }
      if (train.state === 'dwell') {
        train.dwell -= dt;
        const station = train.layout.stations[train.station];
        for (const ticket of this.passengers(train))
          if (ticket.state === 'aboard' && ticket.destination === station) {
            ticket.state = 'alighting';
            ticket.progress = 0;
            ticket.from = ticket.person.p.clone();
            ticket.person.hidden = false;
            ticket.person.state = 'alighting';
            this.record('alighting', train, ticket.person, station);
          }
        if (
          !train.closing &&
          (train.dwell > 2 ||
            this.time < (this.lastDeparture.get(station.id) ?? -Infinity) + this.headway - 2) &&
          this.stationOpen(station) &&
          (train.cars[0].doorOpen ?? 0) > 0.95
        )
          for (const ticket of this.tickets.values())
            if (
              ticket.state === 'waiting' &&
              ticket.origin === station &&
              this.passengers(train).length < 48
            ) {
              const passenger = ticket.person;
              ticket.train = train;
              ticket.carriage = train.cars.reduce(
                (best, c, i) =>
                  c.p.distanceTo(passenger.p) < train.cars[best].p.distanceTo(passenger.p) ? i : best,
                0,
              );
              if (
                this.passengers(train).some(
                  (t) =>
                    t !== ticket &&
                    t.carriage === ticket.carriage &&
                    (t.state === 'boarding' || t.state === 'alighting'),
                )
              ) {
                ticket.train = undefined;
                continue;
              }
              const network = this.population.civil!.network;
              ticket.boardingRoute = network.route(
                network.nearest(passenger.p, `platform:${station.id}`),
                network.nearest(this.door(ticket), `platform:${station.id}`),
                this.time,
              );
              if (!ticket.boardingRoute.length) {
                ticket.train = undefined;
                continue;
              }
              ticket.state = 'boarding';
              ticket.progress = 0;
              ticket.since = this.time;
              passenger.controlled = true;
              passenger.state = 'boarding';
              passenger.detour = undefined;
            }
        const moving = this.passengers(train).some((t) => t.state === 'alighting' || t.state === 'boarding');
        if (
          train.dwell <= 0 &&
          !moving &&
          this.time - (this.lastDeparture.get(station.id) ?? -Infinity) >= this.headway
        ) {
          train.closing = true;
          if ((train.cars[0].doorOpen ?? 0) < 0.02) {
            train.state = 'running';
            train.closing = false;
            this.lastDeparture.set(station.id, this.time);
            this.record('departed', train, undefined, station);
          }
        }
      } else {
        const next = train.layout.stations[train.next],
          stop = next.distance + 12.6,
          remaining = this.forward(train.distance, stop, train.layout);
        let free = Infinity;
        for (let d = 5; d < Math.max(35, (train.speed * train.speed) / 2.8 + 25); d += 5) {
          const sample = railSample(train.layout, train.distance + d);
          if (
            !this.sectionOpen(sample.section.id, train.layout) ||
            this.physics.blocked(sample.point.x, sample.point.z, 1.05, sample.point.y + 1.5)
          ) {
            free = d - 8;
            break;
          }
        }
        for (const other of this.trains)
          if (other !== train && other.layout === train.layout) {
            const gap = this.forward(train.distance, other.distance, train.layout) - 42;
            free = Math.min(free, gap);
          }
        const maxSpeed = Math.sqrt(Math.max(0, 2 * 1.4 * (Math.min(remaining, free) - 0.1))),
          target = Math.min(18, maxSpeed);
        train.speed = Math.max(
          0,
          train.speed + Math.max(-2.4 * dt, Math.min(1.2 * dt, target - train.speed)),
        );
        if (free < 0 && train.speed > 1) {
          this.derail(train, 'Unavoidable track obstruction');
          continue;
        }
        const step = Math.min(remaining, Math.max(0, free), train.speed * dt);
        train.distance = (train.distance + step) % train.layout.length;
        train.state = free < 2 ? 'stopped' : 'running';
        train.reason =
          free < 2
            ? 'Waiting for a clear track section'
            : `Following ${train.layout.name ?? 'Coastal Line'} timetable`;
        if (remaining < 0.15 && train.speed < 0.8) {
          train.distance = stop % train.layout.length;
          train.speed = 0;
          train.station = train.next;
          train.next = (train.next + 1) % train.layout.stations.length;
          train.state = 'dwell';
          train.dwell = 12;
          train.visits++;
          if (train.station === 0) train.circuits++;
          this.record('arrived', train, undefined, next);
        }
      }
      for (let i = 0; i < train.cars.length; i++) {
        const car = train.cars[i],
          sample = railSample(train.layout, train.distance - i * 12.6);
        car.previous.copy(car.p);
        car.previousQ.copy(car.q);
        car.p.set(sample.point.x, sample.point.y, sample.point.z);
        const behind = railSample(train.layout, train.distance - i * 12.6 - 4).point,
          ahead = railSample(train.layout, train.distance - i * 12.6 + 4).point;
        car.yaw = Math.atan2(behind.x - ahead.x, behind.z - ahead.z);
        car.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), car.yaw);
        car.actualSpeed = train.speed;
        car.doorOpen = THREE.MathUtils.lerp(
          car.doorOpen ?? 0,
          train.state === 'dwell' && !train.closing && this.stationOpen(train.layout.stations[train.station])
            ? 1
            : 0,
          Math.min(1, dt * 8),
        );
        car.body.setNextKinematicTranslation(car.p);
        car.body.setNextKinematicRotation(car.q);
      }
    }
    for (const ticket of [...this.tickets.values()]) this.stepTicket(ticket, dt);
  }
  private stepTicket(ticket: Ticket, dt: number): void {
    const person = ticket.person;
    if (['incapacitated', 'falling', 'flee'].includes(person.state)) {
      this.cancel(person.id, 'Trip interrupted by injury');
      return;
    }
    if (ticket.state === 'approaching' || ticket.state === 'waiting') {
      this.reservations.acquire(ticket.queue.id, person.id, this.time, 1, 600);
      if (!this.stationOpen(ticket.origin) || this.time - ticket.since > 480)
        this.cancel(person.id, 'Transit unavailable; planning an alternative');
      return;
    }
    const train = ticket.train!,
      car = train.cars[ticket.carriage!];
    person.previous.copy(person.p);
    if (ticket.state === 'aboard') {
      person.hidden = true;
      person.p.copy(car.p).add(new THREE.Vector3(0, 0.55, 0));
      return;
    }
    const door = this.door(ticket);
    if (ticket.state === 'boarding') {
      const network = this.population.civil!.network;
      const next = ticket.boardingRoute?.[0];
      if (next !== undefined) {
        const node = network.nodes[next];
        if (Math.hypot(node.x - person.p.x, node.z - person.p.z) < 0.13) ticket.boardingRoute!.shift();
        else if (this.population.footClear(person, node)) this.population.walkTo(person, node, dt, 1.5);
        if (this.time - ticket.since < 25) return;
        ticket.boardingRoute = [];
      }
      if (ticket.progress === 0 && person.p.distanceTo(door) > 0.23) {
        if (this.time - ticket.since > 25) {
          person.controlled = false;
          person.detour = undefined;
          ticket.train = undefined;
          ticket.state = 'waiting';
          ticket.since = this.time;
          return;
        }
        if (this.population.footClear(person, door)) this.population.walkTo(person, door, dt, 1.5);
        return;
      }
      ticket.from ??= person.p.clone();
      ticket.progress += dt;
      person.p
        .copy(ticket.from)
        .lerp(car.p.clone().setY(ticket.origin.lift.y), Math.min(1, ticket.progress / 0.9));
      if (ticket.progress >= 0.9) {
        ticket.state = 'aboard';
        person.state = `aboard ${train.layout.name ?? 'Coastal Line'}`;
        person.hidden = true;
        this.reservations.release(person.id);
        this.boardings++;
        this.record('boarded', train, person, ticket.origin);
      }
    } else {
      ticket.progress += dt;
      if (ticket.progress < 1) {
        person.p.copy(ticket.from!).lerp(door, ticket.progress);
        return;
      }
      person.p.copy(door);
      person.layer = `platform:${ticket.destination.id}`;
      person.hidden = false;
      person.controlled = false;
      const nextLeg = ticket.nextLegs?.[0];
      if (nextLeg) {
        const queue = nextLeg.origin.queues.find(
          (p) =>
            this.population.civil!.network.available(p) &&
            this.reservations.acquire(p.id, person.id, this.time, 1, 600),
        );
        if (!queue) {
          person.state = 'waiting for transfer space';
          return;
        }
        this.reservations.release(person.id, ticket.queue.id);
        ticket.nextLegs!.shift();
        ticket.origin = nextLeg.origin;
        ticket.destination = nextLeg.destination;
        ticket.queue = queue;
        ticket.train = undefined;
        ticket.carriage = undefined;
        ticket.state = 'approaching';
        ticket.progress = 0;
        ticket.since = this.time;
        this.transfers++;
        this.record('walking to the connected service platform', train, person, nextLeg.origin);
        this.population.civil?.finishTransit(
          person,
          queue,
          'Changing railway services through the real station entrances',
          ticket.final,
        );
        return;
      }
      this.tickets.delete(person.id);
      this.reservations.release(person.id);
      this.arrivals++;
      this.record('arrived at destination station', train, person, ticket.destination);
      this.population.civil?.finishTransit(
        person,
        ticket.final,
        'Walking from the arrival platform to destination',
      );
    }
  }
  snapshot(): Record<string, number> {
    return {
      trains: this.trains.length,
      trainCarriages: this.trains.reduce((n, t) => n + t.cars.length, 0),
      railBoardings: this.boardings,
      railArrivals: this.arrivals,
      railTransfers: this.transfers,
      railServices: this.layouts.length,
      railPassengers: [...this.tickets.values()].filter((t) => t.state === 'aboard').length,
      railWaiting: this.tickets.size,
      railDisruptions: this.disrupted,
      trainCircuits: this.trains.reduce((n, t) => n + t.circuits, 0),
    };
  }
  inspect(): Record<string, unknown> {
    return {
      headway: this.headway,
      stations: railStations(this.layout).map((s) => ({
        service: s.service,
        interchange: s.interchange,
        id: s.id,
        name: s.name,
        available: this.stationOpen(s),
        queue: this.reservations.size,
      })),
      trains: this.trains.map((t) => ({
        id: t.id,
        distance: t.distance,
        speed: t.speed,
        state: t.state,
        service: t.layout.name ?? 'Coastal Line',
        station: t.layout.stations[t.station].name,
        next: t.layout.stations[t.next].name,
        passengers: this.passengers(t).length,
        reason: t.reason,
        visits: t.visits,
        circuits: t.circuits,
      })),
      tickets: [...this.tickets.values()].map((t) => ({
        person: t.person.id,
        state: t.state,
        from: t.origin.name,
        to: t.destination.name,
        final: t.final.id,
      })),
      log: this.log,
    };
  }
}
